import { Box, Divider, Stack, StackProps, styled, Theme, useTheme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { EntityIdentifier } from '@provision/entity-types';
import { extractMentionsFromRichText } from '@provision/formatting-utils';
import {
  BubbleMenu,
  bubbleMenuMouseDownHandler,
  bubbleMenuShouldShow,
  Editor,
  EditorContent,
  EditorContentProps,
  FilePickerNode,
  getToolbarButtons,
  KEY_SHORTCUTS_MAC,
  KEY_SHORTCUTS_WINDOWS,
  MentionsPopupState,
  RichTextEditorFile,
  RichTextEditorOptions,
  SlashCommandMenu,
  useRichTextEditor,
  UseRichTextEditorProps,
} from '@provision/rich-text-editor';
import {
  Dispatch,
  FocusEvent,
  ForwardedRef,
  forwardRef,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { doNothing } from 'remeda';
import { Subject } from 'rxjs';
import { MENTION_CLASS } from 'src/components/TextWithUI/mentions/const';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { useFileUpload } from 'src/graphql/entities/files-upload';
import { useNotifications } from 'src/utils/hooks';
import { Platform } from 'src/utils/system';
import { AddLinkPopover, useAddLinkPopover } from './AddLinkPopover';
import { BubbleMenuToolbarButton } from './bubble-menu-toolbar-button';
import { SecondaryButton, SendButton } from './buttons';
import { EditorToolbar, ToolbarOptionElement } from './editor-menu';
import { EditorContainer } from './rich-text-editor-container';

const ToolbarContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  overflow: 'auto',
  containerType: 'inline-size',
  flexShrink: 0,
});

export type RichTextEditorProps = Omit<Partial<StackProps>, 'onSubmit' | 'onBlur'> & {
  content?: RichTextEditorOptions['content'];
  onSubmit?: (message: string, mentions?: EntityIdentifier[]) => Promise<void> | void;
  onBlur?: (e: FocusEvent<HTMLDivElement>, editor: Editor | null) => void;
  editable?: boolean;
  editorContentProps?: Omit<Partial<EditorContentProps>, 'editor' | 'ref' | 'onBlur'>;
  editorRef?: MutableRefObject<Editor | null>;
  editorOptions?: Partial<RichTextEditorOptions>;
  placeholderOptions?: UseRichTextEditorProps['placeholderOptions'];
  toolbarOptions?: ToolbarOptionElement[];
  error?: string;
  files?: RichTextEditorFile[];
  setFiles?: Dispatch<SetStateAction<RichTextEditorFile[]>>;
  disabled?: boolean;
  watchContent?: boolean;
  fieldAutoSize?: boolean;
  fieldMinHeight?: string;
  buttonText?: string;
  withSubmitButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonIcon?: string;
  primaryButtonIcon?: string;
  secondaryButtonTooltipText?: React.ReactNode;
  withToolBarOtherOptions?: boolean;
  handleSecondaryButton?: () => void;
  renderPrimaryAction?: (props: {
    isLoading: boolean;
    disabled?: boolean;
    buttonText?: string;
    buttonRef: MutableRefObject<HTMLButtonElement | null>;
    onClick: () => void;
    startIcon?: string;
  }) => ReactNode;
  slots?: {
    contentFooter?: React.ReactNode;
  };
  onGetMentionPopup?: (
    mentionUpdatePopupStateSubject: Subject<Partial<MentionsPopupState>>,
    onMentionKeyDownRef: MutableRefObject<((event: KeyboardEvent) => boolean) | null>,
  ) => ReactNode;
};

const DEFAULT_TOOLBAR_OPTIONS: ToolbarOptionElement[] = [];

const RichTextEditor = forwardRef(
  (
    {
      fieldAutoSize = true,
      fieldMinHeight,
      content = '',
      onSubmit,
      onBlur = doNothing,
      editable = true,
      editorContentProps,
      editorOptions,
      placeholderOptions,
      error,
      buttonText,
      secondaryButtonText,
      secondaryButtonIcon,
      secondaryButtonTooltipText,
      handleSecondaryButton,
      disabled,
      watchContent,
      editorRef,
      withSubmitButton = true,
      withToolBarOtherOptions = true,
      toolbarOptions = DEFAULT_TOOLBAR_OPTIONS,
      slots = {},
      files = [],
      setFiles,
      onGetMentionPopup,
      primaryButtonIcon,
      renderPrimaryAction,
      sx: sxProp,
      ...rest
    }: RichTextEditorProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { contentFooter } = slots;

    const { showErrorAlert } = useNotifications();
    const submitButtonRef = useRef<HTMLButtonElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // State to force re-renders when editor state changes
    const [editorRevision, setEditorRevision] = useState(0);

    const triggerSubmit = useCallback(() => {
      submitButtonRef?.current?.click();
    }, []);

    const submit = useCallback(
      async (message: string, editor: Editor | null) => {
        // If the RichTextEditor is used as a part of a bigger form, it may be useful to return
        // a handle to trigger outside form with a submit event, dispatched from submit button.
        if (onSubmit) {
          await onSubmit(message, editor ? extractMentionsFromRichText(editor.getJSON()) : []);
        }
      },
      [onSubmit],
    );

    const onMentionKeyDownRef = useRef<((event: KeyboardEvent) => boolean) | null>(null);

    const mentionsPopupParams = useMemo(() => {
      return onGetMentionPopup
        ? {
            onMentionKeyDownRef,
            updatePopupStateSubject: new Subject<Partial<MentionsPopupState>>(),
          }
        : undefined;
    }, [onGetMentionPopup]);

    const openFilePicker = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    const openLinkPopoverRef = useRef<(selectedText: string) => void>(() => undefined);

    const slashCommandOptions = useMemo(
      () => ({
        helpers: {
          openFilePicker,
          openLinkPopover: (selectedText: string) => openLinkPopoverRef.current(selectedText),
        },
      }),
      [openFilePicker],
    );

    const { uploadFile } = useFileUpload();
    const { editor, isLoading, handleSubmit, slashCommand } = useRichTextEditor({
      onSubmit: submit,
      triggerSubmit,
      uploadFile,
      onError: showErrorAlert,
      editorOptions: {
        editable,
        content,
        onSelectionUpdate: () => {
          setEditorRevision((prev) => prev + 1);
        },
        onTransaction: () => {
          setEditorRevision((prev) => prev + 1);
        },
        ...editorOptions,
      },
      placeholderOptions,
      watchContent,
      mentionsPopupParams,
      mentionClassName: MENTION_CLASS,
      slashCommandOptions,
    });

    if (editorRef) {
      editorRef.current = editor;
    }

    const bubbleRef = useRef<HTMLElement | null>(null);

    const onBlurCallback = useCallback(
      (e: FocusEvent<HTMLDivElement>) => {
        // We don't want to fire blur event if an element inside Bubble was clicked
        if (!bubbleRef.current?.contains(e.relatedTarget)) {
          onBlur(e, editor);
        }
      },
      [editor, onBlur],
    );

    const theme = useTheme();

    const keyShortcuts = Platform.isMacOS ? KEY_SHORTCUTS_MAC : KEY_SHORTCUTS_WINDOWS;

    const {
      anchorEl: linkPopoverAnchorEl,
      selectedText,
      openPopover: handleAddLink,
      closePopover: handleLinkPopoverClose,
      handleLinkSubmit,
    } = useAddLinkPopover({
      editor,
    });

    useEffect(() => {
      openLinkPopoverRef.current = handleAddLink;
    }, [handleAddLink]);

    type SxEntry =
      | SystemStyleObject<Theme>
      | ((theme: Theme) => SystemStyleObject<Theme>)
      | null
      | false;

    const sxOverrides = useMemo<SxEntry[]>(() => {
      if (!sxProp) {
        return [];
      }

      if (Array.isArray(sxProp)) {
        return sxProp as SxEntry[];
      }

      return [sxProp as SxEntry];
    }, [sxProp]);

    const handleAttachClick = openFilePicker;

    const { formatting: formattingButtons } = useMemo(() => {
      return getToolbarButtons({
        editor,
        keyShortcuts,
        onLinkClick: handleAddLink,
        onAttachClick: handleAttachClick,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, keyShortcuts, handleAddLink, handleAttachClick, editorRevision]);

    const editorContent = useMemo(() => {
      return (
        <EditorContent
          className="editor-content"
          editor={editor}
          {...editorContentProps}
          onBlur={onBlurCallback}
        />
      );
    }, [editor, editorContentProps, onBlurCallback]);

    return (
      <EditorContainer
        {...rest}
        error={!!error}
        ref={ref}
        sx={[
          {
            height: 'auto',
            flexGrow: 1,
            minHeight: 0,
            paddingLeft: '0.4rem',
            paddingRight: '0.4rem',
            '& .editor-content': {
              ...(!fieldAutoSize && {
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
              }),
            },
          },
          ...sxOverrides,
        ]}
      >
        <AddLinkPopover
          open={!!linkPopoverAnchorEl}
          anchorEl={linkPopoverAnchorEl}
          onClose={handleLinkPopoverClose}
          onSubmit={handleLinkSubmit}
          initialTitle={selectedText}
        />
        <SlashCommandMenu
          open={slashCommand.state.isOpen}
          anchorEl={slashCommand.state.anchorEl}
          items={slashCommand.state.items}
          selectedIndex={slashCommand.state.selectedIndex}
          onSelect={slashCommand.selectItem}
          onHighlight={slashCommand.setSelectedIndex}
          onClose={slashCommand.close}
          theme={theme}
        />

        {!!editor && (
          <BubbleMenu editor={editor} shouldShow={bubbleMenuShouldShow}>
            <Box
              ref={bubbleRef}
              onMouseDown={bubbleMenuMouseDownHandler}
              sx={{
                backgroundColor: theme.palette.custom.OV.sys.surface,
                padding: 1,
                boxShadow: theme.shadows[3],
              }}
            >
              {formattingButtons?.map((button) => (
                <BubbleMenuToolbarButton key={button.title} button={button} delay={500} />
              ))}
            </Box>
          </BubbleMenu>
        )}
        {fieldAutoSize ? (
          editorContent
        ) : (
          // this Box occupies the space that parent allows
          // and restricts the editor content to that space
          <Box
            sx={{
              ...(fieldMinHeight && { minHeight: fieldMinHeight }),
              flexGrow: 1,
              position: 'relative',
            }}
          >
            {editorContent}
          </Box>
        )}
        <FilePickerNode
          fileInputRef={fileInputRef}
          onError={showErrorAlert}
          currentFiles={files}
          setCurrentFiles={setFiles}
        />
        {contentFooter}
        <Divider
          sx={{
            my: 1,
            borderColor: (theme) =>
              theme.palette.custom?.OV?.sys?.surfaceBright ?? theme.palette.divider,
            opacity: 0.6,
          }}
        />
        <ToolbarContainer>
          <EditorToolbar
            withToolBarOtherOptions={withToolBarOtherOptions}
            editor={editor}
            toolbarOptions={toolbarOptions}
            toolbarButtonsProps={{
              onAttachClick: handleAttachClick,
            }}
          />
          <Stack direction="row" spacing={2} sx={{ marginLeft: 'auto' }}>
            {!!secondaryButtonText && (
              <Tooltip title={secondaryButtonTooltipText}>
                <SecondaryButton
                  type="button"
                  isLoading={isLoading}
                  disabled={disabled || isLoading}
                  ref={submitButtonRef}
                  onClick={handleSecondaryButton}
                  icon={secondaryButtonIcon}
                  sx={{ marginLeft: 'auto' }}
                  buttonText={secondaryButtonText}
                />
              </Tooltip>
            )}
            {!!withSubmitButton &&
              (renderPrimaryAction ? (
                renderPrimaryAction({
                  isLoading,
                  disabled: disabled || isLoading,
                  buttonText,
                  buttonRef: submitButtonRef,
                  onClick: handleSubmit,
                  startIcon: primaryButtonIcon,
                })
              ) : (
                <SendButton
                  type="submit"
                  isLoading={isLoading}
                  disabled={disabled || isLoading}
                  ref={submitButtonRef}
                  onClick={handleSubmit}
                  buttonText={buttonText}
                  startIcon={primaryButtonIcon}
                />
              ))}
          </Stack>
        </ToolbarContainer>

        {mentionsPopupParams
          ? onGetMentionPopup?.(
              mentionsPopupParams.updatePopupStateSubject,
              onMentionKeyDownRef,
            )
          : null}
      </EditorContainer>
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
